import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { ProfileService } from '../../services/profile.service';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service';
import {
  CustomDatePickerComponent,
  TimeSlot,
} from '../custom-date-picker/custom-date-picker.component';
import { DEFAULT_TIME_SLOTS } from '../../shared/constants/time-slots.constant';
import { mapEmployeesToStaffOptions } from '../../shared/utils/appointment.util';
import { parseTimeToMinutes } from '../../shared/utils/time.util';

interface AppointmentFormData {
  location: string;
  staff: string;
  date: Date | null;
  duration: string;
}

/**
 * AppointmentFormComponent provides a reusable appointment scheduling form that captures location, staff, date/time and duration, integrates with profile and API services to load locations, staff, and location-specific operating hours, and emits form validity and data updates.
 * @component
 * @selector app-appointment-form
 * @example
 *   <app-appointment-form
 *     [initialData]="sampleAppointment"
 *     [packageId]="currentPackageId"
 *     (formValidityChange)="onValidityChange($event)"
 *     (formDataChange)="onFormDataChange($event)">
 *   </app-appointment-form>
 *
 * Purpose:
 *   - Manage a reactive FormGroup for appointment booking with fields: location, staff, date (with custom future-date validation), and duration.
 *   - Load and map available locations and staff from injected services, adapt available time slots based on location operating hours, and reset or update UI state when inputs change.
 *
 * Key lifecycle hooks:
 *   - ngOnInit(): Initializes the reactive form, subscribes to status/value changes to emit initial and subsequent validity and form data, loads locations, and sets previous location tracking.
 *   - ngOnChanges(changes: SimpleChanges): Responds to changes in initialData and packageId to patch form values without emitting change events when appropriate, reload staff/time slots only when the location truly changes, and reload locations when packageId updates.
 *
 * Key methods:
 *   - createForm(): Builds the FormGroup with validators, subscribes to statusChanges and valueChanges to emit formValidityChange and formDataChange, and emits initial validity/data.
 *   - loadLocations(): Subscribes to profileService.locations$ to populate location options and triggers loading locations filtered by packageId.
 *   - onLocationChange(locationId?: string): Loads staff for a selected location, resets date when location changes, fetches location details to update time slots based on operating hours, and falls back to default time slots when no location selected.
 *   - updateTimeSlotsBasedOnLocation(locationData: any): Transforms location operating hours into a lookup map and updates the component's timeSlots array marking each slot available/unavailable based on operating hours.
 *   - isTimeSlotAvailable(timeSlot: string, operatingHoursMap: Map<string, any>): Determines whether a given time slot falls within today's operating hours for the selected location.
 *   - resetTimeSlotsToDefault(): Restores timeSlots to the default 24-hour schedule.
 *   - getFieldError(fieldName: string): Returns user-friendly validation messages for form controls (required, pastDateTime).
 *   - futureDateTimeValidator(control: AbstractControl): Custom validator that ensures selected date/time is in the future and returns a pastDateTime error when invalid.
 *   - parseTimeToMinutes(timeString: string): Helper to convert "HH:MM" strings to minutes for comparisons (wrapper around shared parse utility).
 *
 * Important behaviors/notes:
 *   - Emits formValidityChange (boolean) and formDataChange (AppointmentFormData) initially after form creation and thereafter on status/value changes.
 *   - When initialData changes, the form patches values without re-triggering emissions unless necessary; staff and time slots are reloaded only when the location actually changes.
 *   - Time slot availability logic considers the current day of week and the location's provided open/close times; locations with missing or malformed hours are treated as closed for time-slot availability.
 *
 * @input {AppointmentFormData} initialData - Initial form values used to pre-fill the form (shape: { location: string, staff: string, date: Date | null, duration: string }); changing this patches the form and may trigger staff/time slot reload when location changes.
 * @input {string} packageId - Optional package identifier used when loading available locations; changing this triggers reloading of locations.
 * @output {EventEmitter<boolean>} formValidityChange - Emits a boolean indicating whether the current form is valid; emitted initially and on every validation status change.
 * @output {EventEmitter<AppointmentFormData>} formDataChange - Emits the current form value object whenever the form data changes; emitted initially and on every value change.
 */
@Component({
  selector: 'app-appointment-form',
  templateUrl: './appointment-form.component.html',
  styleUrls: ['./appointment-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CustomDatePickerComponent, ReactiveFormsModule, CommonModule, MatSelectModule],
})
export class AppointmentFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Output() formValidityChange = new EventEmitter<boolean>();
  @Output() formDataChange = new EventEmitter<AppointmentFormData>();
  @Input() initialData: AppointmentFormData = {
    location: '',
    staff: '',
    date: null,
    duration: '',
  };
  @Input() packageId = '';

  appointmentForm!: FormGroup;
  locations: { label: string; value: string }[] = [];
  staffOptions: { label: string; value: string }[] = [];
  locationDetails: any = null; // Store location details for operating hours
  private previousLocation = ''; // Track previous location value

  durationOptions = [
    { label: '30 Minutes', value: '30m' },
    { label: '1 Hour', value: '1h' },
    { label: '1 Hour 30 Minutes', value: '1h30m' },
    { label: '2 Hours', value: '2h' },
    { label: '2 Hours 30 Minutes', value: '2h30m' },
    { label: '3 Hours', value: '3h' },
    { label: '3 Hours 30 Minutes', value: '3h30m' },
    { label: '4 Hours', value: '4h' },
    { label: '4 Hours 30 Minutes', value: '4h30m' },
    { label: '5 Hours', value: '5h' },
    { label: '5 Hours 30 Minutes', value: '5h30m' },
    { label: '6 Hours', value: '6h' },
    { label: '6 Hours 30 Minutes', value: '6h30m' },
    { label: '7 Hours', value: '7h' },
    { label: '7 Hours 30 Minutes', value: '7h30m' },
    { label: '8 Hours', value: '8h' },
  ];

  // Custom time slots for the date picker - 24-hour schedule from 12:00 AM to 11:30 PM
  timeSlots: TimeSlot[] = DEFAULT_TIME_SLOTS.map(s => ({ ...s }));

  constructor(
    private profileService: ProfileService,
    private apiService: ApiService,
    private messageService: NotificationService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.loadLocations();
    // Initialize previous location with current location value
    this.previousLocation = this.initialData.location || '';
    if (this.initialData.location) {
      this.onLocationChange(this.initialData.location);
    }
  }

  /**
   * Handle changes to input properties: updates the appointment form and reloads related data.
   * @example
   * gOnChanges({ initialData: { location: 'Room A' }, packageId: 5 })
   * undefined
   * @param {SimpleChanges} changes - Angular SimpleChanges map of input property changes.
   * @returns {void} No return value; updates the form and triggers side-effect loading as needed.
   **/
  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialData'] && this.appointmentForm) {
      // Update form values when initialData changes
      this.appointmentForm.patchValue(this.initialData, { emitEvent: false });
      // Load staff only if location has actually changed (avoids duplicate fetch when parent echoes form state)
      const loc = this.initialData.location;
      if (loc && loc !== this.previousLocation) {
        this.onLocationChange(loc);
      }
    }

    if (changes['packageId'] && this.packageId) {
      // Reload locations when package ID changes
      this.loadLocations();
    }
  }

  /**
   * Initialize the appointment form, configure controls and validators, subscribe to form status and value changes, and emit initial validity and data.
   * @example
   * this.createForm()
   * void
   * @returns {{void}} No return value; the method initializes the FormGroup and emits form validity and data changes.
   **/
  createForm(): void {
    this.appointmentForm = this.fb.group({
      location: [this.initialData.location, [Validators.required]],
      staff: [this.initialData.staff, [Validators.required]],
      date: [this.initialData.date, [Validators.required, this.futureDateTimeValidator]],
      duration: [this.initialData.duration, [Validators.required]],
    });

    // Emit form validity changes
    this.appointmentForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formValidityChange.emit(this.appointmentForm.valid);
    });

    // Emit form data changes
    this.appointmentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.formDataChange.emit(value);
    });

    // Emit initial validity and data
    this.formValidityChange.emit(this.appointmentForm.valid);
    this.formDataChange.emit(this.appointmentForm.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLocations(): void {
    this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
      if (locations) {
        this.locations = locations.map(loc => ({ label: loc.branch, value: loc.id }));
        this.cdr.markForCheck();
      }
    });
    this.profileService.loadLocations(this.packageId);
  }

  /**
   * Handle change of selected location: load staff, reset date, and update time slots or reset defaults when no location is selected.
   * @example
   * nLocationChange('locationId123')
   * void
   * @param {{string}} {{locationId}} - Optional location ID; if not provided the value is read from the appointment form.
   * @returns {{void}} No return value; updates component state (staff, date, time slots) and triggers API calls.
   **/
  onLocationChange(locationId?: string): void {
    const selectedLocation = locationId || this.appointmentForm.get('location')?.value;
    if (selectedLocation) {
      const locationChanged = selectedLocation !== this.previousLocation;
      this.previousLocation = selectedLocation;

      if (locationChanged) {
        this.staffOptions = [];
        this.appointmentForm.patchValue({ staff: '', date: null });
        this.cdr.markForCheck();
      }

      // Load staff for the selected location
      this.apiService
        .getEmployeesByLocation(selectedLocation)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (this.previousLocation !== selectedLocation) {
              return;
            }
            if (response.status && Array.isArray(response.data)) {
              this.staffOptions = mapEmployeesToStaffOptions(response.data);
            } else {
              this.failStaffLoad();
              return;
            }
            this.cdr.markForCheck();
          },
          error: () => {
            if (this.previousLocation !== selectedLocation) {
              return;
            }
            this.failStaffLoad();
          },
        });

      this.locationDetails = null;
      this.resetTimeSlotsToDefault();

      // Load location details for operating hours
      this.profileService
        .getLocationDetails(selectedLocation)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (this.previousLocation !== selectedLocation) {
              return;
            }
            if (response.status && response.data) {
              this.updateTimeSlotsBasedOnLocation(response.data);
            } else {
              this.failLocationSchedule();
              return;
            }
            this.cdr.markForCheck();
          },
          error: () => {
            if (this.previousLocation !== selectedLocation) {
              return;
            }
            this.failLocationSchedule();
          },
        });
    } else {
      this.previousLocation = '';
      this.staffOptions = [];
      this.appointmentForm.patchValue({ staff: '', date: null });
      this.resetTimeSlotsToDefault();
      this.cdr.markForCheck();
    }
  }

  private notifyError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }

  private failStaffLoad(): void {
    this.staffOptions = [];
    this.notifyError('Failed to load staff for this location');
    this.cdr.markForCheck();
  }

  private failLocationSchedule(): void {
    this.locationDetails = null;
    this.resetTimeSlotsToDefault();
    this.notifyError('Failed to load schedule for this location');
    this.cdr.markForCheck();
  }

  /**
   * Update component timeSlots and locationDetails based on provided location operating hours.
   * @example
   * pdateTimeSlotsBasedOnLocation({ hours: [{ day: 'monday', is_closed: false, open_time: '09:00', close_time: '17:00' }] })
   * undefined
   * @param {{any}} {{locationData}} - Location object containing an 'hours' array of operating hours per day.
   * @returns {{void}} No return value; updates the component's locationDetails and timeSlots.
   **/
  updateTimeSlotsBasedOnLocation(locationData: any): void {
    // Store location details for the custom date picker
    this.locationDetails = locationData;

    if (!locationData.hours || !Array.isArray(locationData.hours)) {
      console.warn('No operating hours data found for location');
      return;
    }

    // Create a map of day names to operating hours
    const operatingHoursMap = new Map();
    locationData.hours.forEach((dayHours: any) => {
      operatingHoursMap.set(dayHours.day.toLowerCase(), {
        isOpen: !dayHours.is_closed,
        openTime: dayHours.open_time,
        closeTime: dayHours.close_time,
      });
    });

    // Update time slots based on operating hours
    this.timeSlots = this.timeSlots.map(slot => {
      const isAvailable = this.isTimeSlotAvailable(slot.time, operatingHoursMap);
      return {
        ...slot,
        available: isAvailable,
      };
    });
  }

  /**
   * Determines whether a given time slot (HH:MM) is available based on today's operating hours.
   * @example
   * isTimeSlotAvailable("14:30", operatingHoursMap)
   * true
   * @param {{string}} {{timeSlot}} - Time slot to check in "HH:MM" format.
   * @param {{Map<string, any>}} {{operatingHoursMap}} - Map of operating hours keyed by weekday name (e.g., "monday") containing openTime, closeTime, and isOpen.
   * @returns {{boolean}} True if the time slot is within today's operating hours and the location is open; otherwise false.
   **/
  private isTimeSlotAvailable(timeSlot: string, operatingHoursMap: Map<string, any>): boolean {
    // Parse the time slot (format: "HH:MM")
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;

    // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
    const today = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[today.getDay()];

    // Get operating hours for the current day
    const dayHours = operatingHoursMap.get(currentDay);

    if (!dayHours || dayHours.isOpen === false) {
      // Location is closed on this day
      return false;
    }

    if (!dayHours.openTime || !dayHours.closeTime) {
      // No specific hours set, assume closed
      return false;
    }

    // Parse opening and closing times
    const openTimeInMinutes = this.parseTimeToMinutes(dayHours.openTime);
    const closeTimeInMinutes = this.parseTimeToMinutes(dayHours.closeTime);

    // Check if the time slot is within operating hours
    return timeInMinutes >= openTimeInMinutes && timeInMinutes <= closeTimeInMinutes;
  }

  private parseTimeToMinutes(timeString: string): number {
    return parseTimeToMinutes(timeString);
  }

  resetTimeSlotsToDefault(): void {
    this.timeSlots = DEFAULT_TIME_SLOTS.map(s => ({ ...s }));
  }

  get showShopTimezoneWarning(): boolean {
    const shopTimezone = this.locationDetails?.timezone;
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !!shopTimezone && !!browserTimezone && shopTimezone !== browserTimezone;
  }

  getFieldError(fieldName: string): string {
    const field = this.appointmentForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['pastDateTime']) return 'Please select a future date and time';
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      location: 'Location',
      staff: 'Staff',
      date: 'Date & Time',
      duration: 'Duration',
    };
    return labels[fieldName] || fieldName;
  }

  /**
   * Validates that the provided form control's value represents a future date/time; returns an error if it's in the past.
   * @example
   * futureDateTimeValidator(control)
   * { pastDateTime: true }
   * @param {AbstractControl} control - The form control whose value will be checked as a date/time.
   * @returns {ValidationErrors | null} Returns a ValidationErrors object with a `pastDateTime` key when the value is not in the future, otherwise null.
   **/
  private futureDateTimeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const selectedDate = new Date(control.value);
    const now = new Date();

    // Check if the selected date/time is in the past
    if (selectedDate <= now) {
      return { pastDateTime: true };
    }

    return null;
  }
}
